import React, { useState, useEffect, useMemo } from "react";
import classNames from "classnames";
import { HOTELSRIVER_OFFICIAL_LOGO_URL as defaultHrLogo } from "../../utils/hotelsRiverBrandLogo";
import FlightLogoLoader from "./FlightLogoLoader";
import { usePublicSiteBranding } from "../../features/vacationPackagesPublic/usePublicSiteBranding";
import "./Loader.scss";

export const Loader = ({
  size = "medium",
  showText = false,
  loaderText,
  inline = false,
  brandLogoUrl: brandLogoUrlProp,
  brandName: brandNameProp,
}) => {
  const [text, setText] = useState("טוען..."); // Initial Hebrew text
  const milisecondBetweenText = 3000;
  const texts = [
    "ברגע זה פנינו לספקים ברחבי העולם",
    "סוכן יקר, אנחנו כבר איתך",
    "התחלנו לקבל מידע, ממשיכים לקבל מחירים...",
    "טוען מידע...",
    "לא לשכוח לעבור פעמיים על פרטי הדרכונים",
    "תודה על ההמתנה, מעריכים את זה",
    "עוד רגע המערכת מציגה מחירונים",
    "המערכת מעדכנת את המידע",
    "תודה על ההמתנה",
  ];

  const publicBranding = usePublicSiteBranding();

  const { effectiveLogoSrc, effectiveAlt, brandTextFallback, fallbackUsed } = useMemo(() => {
    const fromProp =
      brandLogoUrlProp != null && String(brandLogoUrlProp).trim()
        ? String(brandLogoUrlProp).trim()
        : null;
    const fromCtx =
      publicBranding.brandLogoUrl != null && String(publicBranding.brandLogoUrl).trim()
        ? String(publicBranding.brandLogoUrl).trim()
        : null;
    const resolvedName =
      (brandNameProp != null && String(brandNameProp).trim()
        ? String(brandNameProp).trim()
        : null) ||
      (publicBranding.brandName != null && String(publicBranding.brandName).trim()
        ? String(publicBranding.brandName).trim()
        : null);
    const resolved = fromProp || fromCtx;
    if (resolved) {
      return {
        effectiveLogoSrc: resolved,
        effectiveAlt: resolvedName || "לוגו סוכנות",
        brandTextFallback: null,
        fallbackUsed: false,
      };
    }
    // On a public agent site we must NEVER show the HotelsRiver brand — not the logo
    // and not the "HOTELS RIVER" text. Use the agency name if known, otherwise show
    // no brand (just the loading animation) until the agency logo resolves.
    if (publicBranding.requiresPublicSite) {
      return {
        effectiveLogoSrc: null,
        effectiveAlt: resolvedName || "לוגו סוכנות",
        brandTextFallback: resolvedName || "",
        fallbackUsed: false,
      };
    }
    return {
      effectiveLogoSrc: defaultHrLogo,
      effectiveAlt: "Hotels River Logo",
      brandTextFallback: undefined,
      fallbackUsed: true,
    };
  }, [
    brandLogoUrlProp,
    brandNameProp,
    publicBranding.brandLogoUrl,
    publicBranding.brandName,
    publicBranding.requiresPublicSite,
  ]);

  useEffect(() => {
    if (import.meta.env.DEV && publicBranding.requiresPublicSite) {
      const hasAgencyLogo = Boolean(effectiveLogoSrc);
      console.log("[PUBLIC_LOADER_BRAND]", {
        hasAgencyLogo,
        logoUrl: effectiveLogoSrc || null,
        brandTextFallback: brandTextFallback || null,
        hotelsRiverFallbackUsed: fallbackUsed,
        publicSitePending: publicBranding.publicSitePending,
        siteIdentityStatus: publicBranding.siteIdentityStatus,
      });
    }
  }, [
    effectiveLogoSrc,
    brandTextFallback,
    fallbackUsed,
    publicBranding.publicSitePending,
    publicBranding.requiresPublicSite,
    publicBranding.siteIdentityStatus,
  ]);

  useEffect(() => {
    if (showText) {
      let index = 0;
      const intervalId = setInterval(() => {
        index = (index + 1) % texts.length;
        setText(texts[index]);
      }, milisecondBetweenText);
      return () => clearInterval(intervalId);
    }
  }, []);

  const captionText = loaderText || "טוענים תוצאות";
  const isCompact = inline || size === "small";
  const isPublicCover = publicBranding.requiresPublicSite && !inline;

  return (
    <div
      className={classNames("loader-container", "loader-container--flight", {
        "loader-inline": inline,
        [`loader-size-${size}`]: Boolean(size),
        "loader-container--public-cover": isPublicCover,
        "public-site-loading-cover": isPublicCover,
      })}
    >
      <FlightLogoLoader
        logoSrc={effectiveLogoSrc}
        logoAlt={effectiveAlt}
        brandTextFallback={brandTextFallback}
        caption={captionText}
        compact={isCompact}
        showCaption
      />
      {showText && !loaderText && <div className="loader-text">{text}</div>}
    </div>
  );
};
